import { sortBy as _sortBy } from 'lodash';

import type {
  DateOperatorFields,
  DateOperatorFilter,
  LineItemEndDateFilter,
  RangeFilter,
} from '../../../../apis/graphql';
import { ComaprisonOperation, DateOperator } from '../../../../apis/graphql';
import type { DropdownOption } from '../../../../common/Dropdown';
import { DEFAULT_EMPTY_NUMBER_FILTER_VALUE } from '../../../../contexts/FilterContext/hooks';
import { isRelativeDateFilterFilled } from '../../../../contexts/FilterContext/hooks/utils';
import type { Nullable } from '../../../../models';
import { amountToMicroCurrency, microCurrencyToAmount } from '../../../../utils/formatting';
import { isComparisonDateOperator } from '../../FilterPanel/FilterRow/DateControl/types';
import { filtersWithFixedOrderConfig } from './config';
import type {
  AdditionalCompletionsRemainingFilter,
  AdditionalFilterBoolean,
  AdditionalFilterCalendar,
  AdditionalFilterDropdown,
  AdditionalFilterInDropdown,
  AdditionalFilterRange,
  AdditionalLineItemEndDateFilter,
  FilterValue,
  SelectedFiltersValues,
} from './constants';
import {
  AdditionalFilterType,
  ComparisonFilterSubType,
  FILTER_LABELS_WITH_FIXED_ORDER,
  PERCENT_MULTIPLIER,
} from './constants';

export const getSortedFiltersWithDynamicOrder = (
  filters: AdditionalFilterInDropdown[],
  filterLabelsWithFixedOrder: string[]
): AdditionalFilterInDropdown[] => {
  const dynamicFilters = filters.filter((filter) => !filterLabelsWithFixedOrder.includes(filter.label));

  return _sortBy(dynamicFilters, 'label');
};

export const getSortedFilters = (filters: AdditionalFilterInDropdown[]): AdditionalFilterInDropdown[] => {
  const sortedFiltersWithDynamicOrder = getSortedFiltersWithDynamicOrder(filters, FILTER_LABELS_WITH_FIXED_ORDER);

  return [...filtersWithFixedOrderConfig, ...sortedFiltersWithDynamicOrder];
};

export const isDropdownOptionValues = (value?: Nullable<FilterValue | string>): value is DropdownOption[] =>
  Array.isArray(value) && value.every((value) => typeof value === 'object');

export const isSimpleDropdownValues = (value?: Nullable<FilterValue | string>): value is string[] =>
  Array.isArray(value) && value.every((value) => typeof value === 'string');

// Legacy COMPARISON shape. During the KATANA-1188 window lineItemEndDate resolves to the
// flat LineItemEndDateFilter type, so GQL always injects __typename: 'LineItemEndDateFilter'
// for both shapes — discriminate on the comparisonOperation field value, not __typename.
// Works for GQL responses (all fields present, comparison fields null on calendar values)
// and for panel-produced values (only the relevant fields set).
export const isLineItemEndDateFilterValue = (value?: Nullable<FilterValue | string>): value is LineItemEndDateFilter =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  !!(value as LineItemEndDateFilter).comparisonOperation;

// New CALENDAR shape. A value is calendar-shaped when it has an operator and is not the
// legacy comparison shape. Narrows to the shared DateOperatorFields interface (operator,
// date, startDate, endDate) so it stays field-agnostic and applies to any future
// date-operator filter, not just lineItemEndDate. Covers both GQL responses and
// DateControl panel output ({ operator, date } / { operator, startDate, endDate }).
export const isCalendarDateFilterValue = (
  value?: Nullable<FilterValue | DateOperatorFields | string>
): value is DateOperatorFields =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  !(value as LineItemEndDateFilter).comparisonOperation &&
  !!(value as DateOperatorFields).operator;

// Accepts either shape. Discriminate the legacy COMPARISON shape via
// isLineItemEndDateFilterValue, otherwise treat as the new CALENDAR shape.
export const isLineItemEndDateFilterFilled = (
  value?: Nullable<LineItemEndDateFilter | DateOperatorFilter>
): boolean => {
  if (!value) return false;
  if (isLineItemEndDateFilterValue(value)) {
    return value.duration != null && value.duration !== DEFAULT_EMPTY_NUMBER_FILTER_VALUE && value.duration > 0;
  }
  // A relative filter has no date/startDate/endDate, so it is checked on its own fields. A
  // comparison operator (Is Before/After/On or Before/On or After) carrying a conditional
  // boundary (KATANA-1752) is the same shape minus the operator, so it's checked the same way —
  // without this, a complete conditional triple falls through to the `!!value.date` check below,
  // which is always false for a conditional value, so the filter never gets a chip even though it
  // is applied and forwarded to campaign-searcher.
  if (
    value.operator === DateOperator.IsRelativeToToday ||
    (value.direction && value.operator && isComparisonDateOperator(value.operator))
  ) {
    return isRelativeDateFilterFilled(value);
  }
  // New CALENDAR shape (DateOperatorFilter): filled when a single date or a full range is set.
  return !!value.date || (!!value.startDate && !!value.endDate);
};

export const isRangeFilterValue = (value?: Nullable<FilterValue | string>): value is RangeFilter =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  ('min' in value || 'max' in value || 'minOperator' in value || 'maxOperator' in value);

export const isBooleanFilterValue = (value?: Nullable<FilterValue | string>): value is boolean =>
  typeof value === 'boolean';

// KATANA-1811: IS_EMPTY / IS_NOT_EMPTY are unary. They ride minOperator with no bound in either slot
// (campaign-graphql KATANA-1810, campaign-searcher KATANA-1809), so a committed existence filter is
// { min: null, max: null, minOperator: IS_EMPTY }: an operator with nothing beside it.
const EXISTENCE_COMPARISON_OPERATIONS: ReadonlySet<string> = new Set([
  ComaprisonOperation.IsEmpty,
  ComaprisonOperation.IsNotEmpty,
]);

export const isExistenceComparisonOperation = (operation?: Nullable<string>): boolean =>
  !!operation && EXISTENCE_COMPARISON_OPERATIONS.has(operation);

// A range filter is filled when it carries a bound, or when it carries an existence operator, which
// has no bound by design. Without the second clause every caller (query variables, shareable-link
// save, chip registry, chip removal) treats an IS_EMPTY filter as unset and silently drops it.
// Typed to the range fields alone, so both the committed RangeFilterInput and the RangeFilter
// read back from GraphQL can be passed without a cast.
export const isRangeFilterFilled = (
  value?: Nullable<Partial<Pick<RangeFilter, 'min' | 'max' | 'minOperator' | 'maxOperator'>>>
): boolean => {
  return !!value && (value.min != null || value.max != null || isExistenceComparisonOperation(value.minOperator));
};

export const isAdditionalFilterLineItemEndDate = (
  value: AdditionalFilterInDropdown
): value is AdditionalLineItemEndDateFilter => {
  return value.type === AdditionalFilterType.COMPARISON && value.subType === ComparisonFilterSubType.LINE_ITEM_END_DATE;
};

export const isAdditionalBooleanFilter = (filter: AdditionalFilterInDropdown): filter is AdditionalFilterBoolean => {
  return filter.type === AdditionalFilterType.BOOLEAN;
};

export const isAdditionalBooleanFilterValue = (
  filter: AdditionalFilterInDropdown,
  selectedValue?: Nullable<FilterValue>
): selectedValue is Nullable<boolean> | undefined => {
  return filter.type === AdditionalFilterType.BOOLEAN;
};

export const isAdditionalFilterCompletionsRemaining = (
  value: AdditionalFilterInDropdown
): value is AdditionalCompletionsRemainingFilter => {
  return (
    value.type === AdditionalFilterType.COMPARISON && value.subType === ComparisonFilterSubType.COMPLETIONS_REMAINING
  );
};

export const isAdditionalFilterDropdown = (value: AdditionalFilterInDropdown): value is AdditionalFilterDropdown => {
  return (
    value.type === AdditionalFilterType.CHECKBOX ||
    value.type === AdditionalFilterType.DROPDOWN ||
    value.type === AdditionalFilterType.RADIOBUTTON ||
    Array.isArray(value.selectedFilterValues)
  );
};

export const isAdditionalFilterRange = (value: AdditionalFilterInDropdown): value is AdditionalFilterRange => {
  return value.type === AdditionalFilterType.RANGE || isRangeFilterValue(value.selectedFilterValues);
};

export const isAdditionalFilterCalendar = (value: AdditionalFilterInDropdown): value is AdditionalFilterCalendar => {
  return value.type === AdditionalFilterType.CALENDAR;
};

export const setSearchParamsForIncludeExcludeDropdowns = (
  filters: AdditionalFilterInDropdown[],
  selectedFiltersValues: SelectedFiltersValues
): AdditionalFilterInDropdown[] => {
  return filters.map((filter) => {
    if (filter.includeExcludeOptions) {
      const excludeFilterValues = selectedFiltersValues[filter.includeExcludeOptions.exclude];
      if (
        excludeFilterValues &&
        (isDropdownOptionValues(excludeFilterValues) || isSimpleDropdownValues(excludeFilterValues)) &&
        excludeFilterValues.length
      ) {
        filter.searchParam = filter.includeExcludeOptions.exclude;
      } else {
        filter.searchParam = filter.includeExcludeOptions.include;
      }
    }

    return filter;
  });
};

export function getPercentPacingValueToDisplay(value: number | null): number | null {
  return value !== null ? Math.round(value * PERCENT_MULTIPLIER) : null;
}

export function getPercentPacingValueToSave(value: number | null): number | null {
  return value !== null ? value / PERCENT_MULTIPLIER : null;
}

export function getCPMValueToDisplay(value: number | null): number | null {
  return value !== null ? microCurrencyToAmount(value, true) : null;
}

export function getCPMValueToSave(value: number | null): number | null {
  return value !== null ? amountToMicroCurrency(value) : null;
}
